import { RefObject } from 'react'
import { Modal } from '@magnetic/modal'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Button } from '@magnetic/button'
import { Input } from '@magnetic/input'
import { Toggle } from '@magnetic/switch'
import { Spinner } from '@magnetic/spinner'
import { useToast } from '@magnetic/notification'
import { ArrowsClockwise, ArrowSquareOut, X } from '@/lib/icons'
import type { LogEntry } from '@/hooks/useApplicationLogs'

interface ApplicationLogsModalProps {
  isOpen: boolean
  onClose: () => void
  logs: LogEntry[]
  logsError: string | null
  logsStreaming: boolean
  followLogs: boolean
  setFollowLogs: (follow: boolean) => void
  tailLines: number
  setTailLines: (lines: number) => void
  logsFullScreen: boolean
  setLogsFullScreen: (fullScreen: boolean) => void
  activePod: string
  activeContainer: string
  startLogsStream: () => void
  clearLogs: () => void
  logsContainerRef: RefObject<HTMLDivElement>
}

export function ApplicationLogsModal({
  isOpen,
  onClose,
  logs,
  logsError,
  logsStreaming,
  followLogs,
  setFollowLogs,
  tailLines,
  setTailLines,
  logsFullScreen,
  setLogsFullScreen,
  activePod,
  activeContainer,
  startLogsStream,
  clearLogs,
  logsContainerRef,
}: ApplicationLogsModalProps) {
  const { addToast } = useToast()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={logsFullScreen ? 1200 : 'lg'}
    >
      <Flex direction="vertical" gap="lg" style={{ padding: 24, height: logsFullScreen ? '100%' : undefined }}>
        <Flex align="center" justify="space-between">
          <Flex direction="vertical" gap="xs">
            <Heading size="section">Application Logs</Heading>
            <Text size="p4" color="light">
              {activePod ? `Pod: ${activePod}` : 'Pod: auto'}
              {activeContainer ? ` · Container: ${activeContainer}` : ''}
            </Text>
          </Flex>
          <Flex align="center" gap="sm">
            <Flex align="center" gap="xs">
              <Text size="p4" color="light">Follow</Text>
              <Toggle checked={followLogs} onClick={() => setFollowLogs(!followLogs)} />
            </Flex>
            <Button
              kind="tertiary"
              size="sm"
              icon={<ArrowsClockwise size={16} />}
              onClick={startLogsStream}
              disabled={logsStreaming}
            />
            <Button
              kind="tertiary"
              size="sm"
              icon={<ArrowSquareOut size={16} />}
              onClick={() => setLogsFullScreen(!logsFullScreen)}
            >
              {logsFullScreen ? 'Exit full screen' : 'Full screen'}
            </Button>
            <Button
              kind="tertiary"
              size="sm"
              icon={<X size={16} />}
              onClick={onClose}
            />
          </Flex>
        </Flex>

        <Flex align="center" justify="space-between" gap="sm">
          <Input
            label="Tail lines"
            hiddenLabel
            type="number"
            min={10}
            max={1000}
            value={String(tailLines)}
            onChange={(event) => {
              const next = Number(event.target.value)
              setTailLines(Number.isFinite(next) ? Math.max(10, Math.min(1000, next)) : 200)
            }}
            style={{ maxWidth: 160 }}
          />
          <Flex gap="sm">
            <Button kind="secondary" size="sm" onClick={clearLogs}>
              Clear
            </Button>
            <Button
              kind="secondary"
              size="sm"
              onClick={() => {
                const text = logs.map((entry) => entry.line).join('\n')
                navigator.clipboard.writeText(text)
                addToast({
                  status: 'positive',
                  title: 'Logs copied',
                  children: 'Log contents copied to clipboard.',
                })
              }}
              disabled={logs.length === 0}
            >
              Copy
            </Button>
          </Flex>
        </Flex>

        <Modal.Content>
          <Flex
            direction="vertical"
            ref={logsContainerRef}
            style={{
              maxHeight: logsFullScreen ? 'calc(100vh - 320px)' : 420,
              overflow: 'auto',
              border: '1px solid var(--mds-color-theme-outline-secondary-normal)',
              borderRadius: 8,
              backgroundColor: 'var(--mds-color-theme-background-solid-primary-normal)',
              padding: 16,
              fontFamily: 'monospace',
              fontSize: '0.8125rem',
            }}
          >
            {logsError ? (
              <Text size="p4" color="light">{logsError}</Text>
            ) : logsStreaming && logs.length === 0 ? (
              <Flex align="center" justify="center" style={{ padding: 32 }}>
                <Spinner size="md" aria-label="Streaming logs" />
              </Flex>
            ) : logs.length === 0 ? (
              <Text size="p4" color="light">No logs available.</Text>
            ) : (
              <Flex direction="vertical" gap="xs">
                {logs.map((entry) => (
                  <Flex key={entry.id} direction="vertical" gap="xs" style={{ wordBreak: 'break-word' }}>
                    <Text size="xs" color="light">
                      {entry.timestamp ? `[${entry.timestamp}] ` : ''}
                      {entry.pod ? `${entry.pod}` : 'pod'}
                      {entry.container ? `/${entry.container}` : ''}
                    </Text>
                    <Text size="p4">{entry.line}</Text>
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>
        </Modal.Content>
      </Flex>
    </Modal>
  )
}
